/**
 * Controls the update of the list of tariffs
 * when questions it depends on are answered
 * @module embed/tariff_list
 */

import { delegate } from '@/lib/events';
import { busyWhile } from '@/lib/busy';
import { renderOptions } from '@/lib/forms';
import {
  nameSelector,
  getSupplier,
  getPostcode,
  hasDualFuel,
  hasEconomy7,
  getPayMethod
} from './form';
import { renderIsFilled } from './is_filled';
import withErrorRender from '@/lib/withErrorRender';
import withEvents from '@/lib/withEvents';
import { ERROR_MESSAGE } from '@/../packs/embed';
import { isPostcodeValid } from './form_validation';
import isEqual from 'lodash/isEqual';
import { hide, show } from '@/lib/show_hide';
import { EVENT_SUPPLIER_LIST_UPDATE } from './supplier_list';

const SELECTOR = '.js-tariff-list';
const SELECTOR_MESSAGE = '.js-tariff-list__availability-message';
const SELECTOR_SELECT = 'select';
const ATTR_FUEL_TYPE = 'data-fuel-type';
export const ATTR_HAS_END_DATE = 'data-has-end-date';
export const EVENT_TARIFF_LIST_UPDATE = 'tariff-list-update';

const CLASS_NO_TARIFFS = 'bcs-tariffList--noTariffs';

export const ERROR_NO_POSTCODE = 'missing_postcode';
export const ERROR_INVALID_POSTCODE = 'invalid_postcode';
export const ERROR_INVALID_SUPPLIER = 'no_supplier';

function renderOptionEls(tariffs) {
  return tariffs
    .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
    .map(
      ({ id, name, tariffEndDate }) =>
        `<option value="${id}" ${ATTR_HAS_END_DATE}="${tariffEndDate}">${name}</option>`
    );
}

function setupTariffList(scope, el, apiClient, validator) {
  const fuelType = el.getAttribute(ATTR_FUEL_TYPE);

  let lastOptions = {};
  function loadTariffs(criteria) {
    // Avoid fetching again and again the same criteria
    if (isEqual(lastOptions, criteria)) return Promise.resolve();
    lastOptions = criteria;

    // Prevent fetching if criteria are invalid
    const errorMessage = hasInvalidCriteria(criteria, validator);
    if (errorMessage) return Promise.resolve();

    return busyWhile(
      el,
      withErrorRender(
        withEvents(
          apiClient
            .getTariffList(criteria)
            .then(tariffs => {
              if (tariffs.length > 0) {
                const optionList = renderOptionEls(tariffs);
                const select = el.querySelector(SELECTOR_SELECT);
                renderOptions(select, optionList.join(''));
                return renderIsFilled(select);
              } else {
                throw new Error('No tariffs listed');
              }
            })
            .then(() => {
              el.classList.remove(CLASS_NO_TARIFFS);
              el.querySelector(SELECTOR_SELECT).disabled = false;
              return;
            }),
          {
            scope: el,
            eventName: EVENT_TARIFF_LIST_UPDATE
          }
        ),
        {
          scope: el,
          message: ERROR_MESSAGE,
          beforeSelector: '.form-group'
        }
      )
    );
  }

  function getTariffCriteria() {
    return {
      postcode: getPostcode(scope),
      supplier_id: getSupplier(scope, fuelType),
      economy7: fuelType == 'gas' ? false : hasEconomy7(scope),
      fuel_type: fuelType,
      dual_fuel: hasDualFuel(scope),
      current_pay_method: getPayMethod(scope, fuelType)
    };
  }

  function updateAvailableTariffs() {
    return loadTariffs(getTariffCriteria());
  }

  function updateAvailabilityNoticeVisibility() {
    const errorMessage = hasInvalidCriteria(getTariffCriteria(), validator);
    const select = el.querySelector(SELECTOR_SELECT);
    if (errorMessage) {
      el.classList.add(CLASS_NO_TARIFFS);
      show(el.querySelector(SELECTOR_MESSAGE));
      select.value = '';
      select.disabled = true;
    } else {
      hide(el.querySelector(SELECTOR_MESSAGE));
    }
    return errorMessage;
  }

  // A timeout to debounce the loading of tariffs
  let loadTimeout;
  // A timeout to debounce the update of the visibility notice
  let availabilityTimeout;
  scope.addEventListener(
    'change',
    delegate(
      `${nameSelector('supplier', fuelType)},
       ${nameSelector('postcode')},
       ${nameSelector('dual_fuel')},
       ${nameSelector('fuel_type')},
       ${nameSelector('economy7')},
       ${nameSelector('current_pay_method', fuelType)}`,
      () => {
        clearTimeout(availabilityTimeout);
        availabilityTimeout = setTimeout(updateAvailabilityNoticeVisibility, 0);
        clearTimeout(loadTimeout);
        loadTimeout = setTimeout(scheduleTariffUpdate, 0);
      }
    )
  );

  scope.addEventListener(
    'keydown',
    delegate(
      `${nameSelector('postcode')}`,
      event => {
        if (event.keyCode == 13) {
          clearTimeout(loadTimeout);
          clearTimeout(availabilityTimeout);
          // Instantly update tariffs
          updateAvailabilityNoticeVisibility();
          scheduleTariffUpdate();
        }
      },
      // Make sure this happens before submission even starts
      true
    )
  );

  scope.addEventListener(
    'input',
    delegate(`${nameSelector('postcode')}`, () => {
      clearTimeout(availabilityTimeout);
      availabilityTimeout = setTimeout(updateAvailabilityNoticeVisibility, 75);
      clearTimeout(loadTimeout);
      loadTimeout = setTimeout(scheduleTariffUpdate, 1000);
    })
  );

  // Handle concurency with supplier requests
  // Because supplier requests might clear the selected supplier
  // (change of postcode or eco 7 noticeably)

  // Dirty flag to store if we're fetching tariffs
  let fetchingTariffs;
  // Flag to know if an update is scheduled for when tariffs
  // have finished fetching
  let updateScheduled;

  // Run update and clean schedule
  function runScheduledUpdate() {
    updateAvailableTariffs();
    updateScheduled = false;
  }

  // Schedule a tariff update
  function scheduleTariffUpdate() {
    if (!fetchingTariffs) {
      runScheduledUpdate();
    } else {
      updateScheduled = true;
    }
  }

  // Coordinate with the tariff fetching
  scope.addEventListener(
    `${EVENT_SUPPLIER_LIST_UPDATE}:before`,
    delegate(`[${ATTR_FUEL_TYPE}="${fuelType}"]`, function() {
      fetchingTariffs = true;
    })
  );
  scope.addEventListener(
    `${EVENT_SUPPLIER_LIST_UPDATE}:complete`,
    delegate(`[${ATTR_FUEL_TYPE}="${fuelType}"]`, function() {
      fetchingTariffs = false;
      if (updateScheduled) {
        runScheduledUpdate();
      }
      // Check if the tariff selection has disappeared
      // in which case we need to notify the user to pick
      // a new one
      updateAvailabilityNoticeVisibility();
    })
  );

  // TODO: Expect this to have been already taken care off by the backend
  return updateAvailableTariffs().then(function() {
    const event = new CustomEvent('didSetupTariff', { bubbles: true });
    return el.dispatchEvent(event);
  });
}

export default function setupTariffLists(el, apiClient, validator) {
  Array.from(el.querySelectorAll(SELECTOR)).forEach(widget =>
    setupTariffList(el, widget, apiClient, validator)
  );
}

function hasInvalidCriteria(criteria, validator) {
  if (!criteria.postcode) return ERROR_INVALID_POSTCODE;
  if (!isPostcodeValid(criteria.postcode, validator))
    return ERROR_INVALID_POSTCODE;
  if (!criteria.supplier_id) return ERROR_INVALID_SUPPLIER;
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/tariff_list.js